import { observer } from "mobx-react-lite";
import { FC, useEffect, useRef } from "react";
import { useFormState } from "react-final-form";

import { Heading, Separator, Stack, Text } from "@bps/fluent-ui";
import {
  XeroFetchedAccountDto,
  XeroFinancialSyncStatus
} from "@libs/gateways/custom-integration/CustomIntegrationXeroGateway.dtos.ts";
import { nameOfFactory } from "@libs/utils/name-of.utils.ts";
import { AccountMapping } from "@stores/custom-integration/models/AccountMapping.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import { DropdownField } from "@ui-components/form/DropdownField.tsx";
import { SubmissionForm } from "@ui-components/form/submission-form/SubmissionForm.tsx";
import { SubmissionFormProps } from "@ui-components/form/submission-form/SubmissionForm.types.ts";

import { useIntegrationScreenContext } from "../context/IntegrationScreenContext.ts";
import { displayOptions } from "./utils.ts";
import { XeroAccountMappingFromValidator } from "./XeroAccountMappingFromValidator.ts";

const sepStyles = {
  root: { padding: "2px 0", height: 0 },
  content: { display: "block" } // prevents unnecessary scrollbar
};

const nameOf = nameOfFactory<XeroAccountMappingFormValues>();

enum FetchAccountOptions {
  Bank = "BANK",
  Credits = "CURRLIAB"
}
export type XeroAccountMappingFormValues = {
  invoiceAccountId?: string;
  paymentAccountId?: string;
  unallocatedAccountId?: string;
};

export const accountsValid = (
  values: XeroAccountMappingFormValues | AccountMapping | undefined
): boolean => {
  if (!values) return false;

  return (
    !!values.paymentAccountId &&
    !!values.invoiceAccountId &&
    !!values.unallocatedAccountId
  );
};

const validator = new XeroAccountMappingFromValidator();

const ValidityListener = (props: {
  onValidityChange: (arg0: boolean) => void;
}) => {
  const {
    values: { paymentAccountId, invoiceAccountId, unallocatedAccountId }
  } = useFormState<XeroAccountMappingFormValues>();

  const isValid =
    !!paymentAccountId && !!invoiceAccountId && !!unallocatedAccountId;

  const { onValidityChange } = props;

  useEffect(() => {
    onValidityChange(isValid);
  }, [isValid, onValidityChange]);

  return null;
};

export const XeroAccountMappingForm: FC<{
  fetchedAccountData: XeroFetchedAccountDto[];
  accountMappings?: AccountMapping;
  onValidityChange?: (isValid: boolean) => void;
}> = observer(({ fetchedAccountData, accountMappings, onValidityChange }) => {
  const { notification, customIntegrationXero } = useStores();
  const { xeroTenantId } = useIntegrationScreenContext();

  if (!xeroTenantId) return null;

  const handleSubmit: SubmissionFormProps<XeroAccountMappingFormValues>["onSubmit"] =
    async (values, form) => {
      // only use no provider id one as the default one to get the account mapping
      const mapping =
        accountMappings ??
        Array.from(customIntegrationXero.accountMappingMap.values()).find(
          x => x.targetTenantId === xeroTenantId && !x.providerId
        );

      if (!xeroTenantId) {
        notification.error("Please select an organisation first.");
        return;
      }
      if (mapping) {
        await customIntegrationXero.updateAccountMappings({
          ...values,
          id: mapping.id,
          targetTenantId: mapping.targetTenantId,
          eTag: mapping.eTag,
          changeLog: mapping.changeLog
        });
        notification.success("Account mappings have been updated.");
      } else {
        await customIntegrationXero.addAccountMappings({
          ...values,
          targetTenantId: xeroTenantId
        });
        notification.success("Account mappings have been saved.");
      }
      if (form) {
        form.reset(values);
      }
    };

  const invoiceOptions = displayOptions(
    fetchedAccountData,
    x =>
      x.type !== FetchAccountOptions.Bank &&
      x.type !== FetchAccountOptions.Credits
  );

  const creditsOptions = displayOptions(
    fetchedAccountData,
    x => x.type === FetchAccountOptions.Credits
  );

  const paymentsOptions = displayOptions(
    fetchedAccountData,
    x => x.type === FetchAccountOptions.Bank || x.enablePaymentsToAccount
  );

  const initialValues = useRef<XeroAccountMappingFormValues>({
    paymentAccountId: accountMappings?.paymentAccountId,
    invoiceAccountId: accountMappings?.invoiceAccountId,
    unallocatedAccountId: accountMappings?.unallocatedAccountId
  });

  return (
    <SubmissionForm<XeroAccountMappingFormValues>
      onSubmit={handleSubmit}
      formName="xero-acccount-mapping-form"
      buttonsProps={{
        disableCancelOnPristine: true,
        disableSubmitOnPristine: true
      }}
      validate={validator.validate}
      initialValues={initialValues.current}
      disabled={
        customIntegrationXero.getFinancialSyncRequestStatus !==
          XeroFinancialSyncStatus.Completed &&
        customIntegrationXero.getFinancialSyncRequestStatus !== undefined
      }
    >
      {() => {
        return (
          <Stack
            tokens={{ childrenGap: 8 }}
            styles={{ root: { width: 400, paddingTop: 24 } }}
          >
            {onValidityChange && (
              <ValidityListener onValidityChange={onValidityChange} />
            )}
            <Stack horizontal verticalAlign="center" horizontalAlign="start">
              <Heading
                styles={{
                  root: { width: 200, paddingLeft: 8 }
                }}
              >
                Transaction type
              </Heading>
              <Heading>Xero account code</Heading>
            </Stack>
            <Separator styles={sepStyles} />
            <Stack
              styles={{ root: { paddingLeft: 8 } }}
              horizontal
              verticalAlign="center"
              horizontalAlign="space-between"
            >
              <Text>Invoices </Text>
              <DropdownField
                name={nameOf("invoiceAccountId")}
                options={invoiceOptions || []}
                styles={{
                  root: { width: 200 },
                  callout: { height: 300 }
                }}
              />
            </Stack>
            <Separator styles={sepStyles} />
            <Stack
              styles={{ root: { paddingLeft: 8 } }}
              verticalAlign="center"
              horizontal
              horizontalAlign="space-between"
            >
              <Text>Payments</Text>
              <DropdownField
                name={nameOf("paymentAccountId")}
                options={paymentsOptions}
                styles={{
                  root: { width: 200 },
                  callout: { height: 300 }
                }}
              />
            </Stack>
            <Separator styles={sepStyles} />
            <Stack
              styles={{ root: { paddingLeft: 8 } }}
              verticalAlign="center"
              horizontal
              horizontalAlign="space-between"
            >
              <Text>Credits</Text>
              <DropdownField
                name={nameOf("unallocatedAccountId")}
                options={creditsOptions}
                styles={{
                  root: { width: 200 },
                  callout: { height: 300 }
                }}
              />
            </Stack>
            <Separator styles={sepStyles} />
          </Stack>
        );
      }}
    </SubmissionForm>
  );
});
